import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { KatButton, KatDropdown, KatIcon, KatInput, KatLabel, KatOption, } from "@amzn/katal-react";
import {
    GetCurrentCountryCodeResponse,
    GetProductDataResponse,
    GetProductPageDataResponse, GetSelectedProductResponse,
    ProductInfo,
    ProductInfoResult
} from "src/model/ProductMatch";
import { setProductDataQueryStart } from "src/actions/ProductSearchAction";
import RequestStatus from "src/model/RequestStatus";
import MetricsUtil, {ActionName} from "src/utils/MetricsUtil";
import ProductDetailComponent from "src/components/product/ProductDetailComponent";
import ApplicationState from "src/model/ApplicationState";
import ErrorStatusComponent from "../common/ErrorStatusComponent";
import ProductListComponent from "src/components/product/ProductListComponent";
import { isLoginMode, getCountryListConfig } from "src/config/Config";
import { removeQueryProductInfo } from "src/actions/QueryProductUpdateAction";
import { removeSelectedProduct, setSelectedProductQueryStart } from "src/actions/SelectedProductQueryAction";
import {removeRecommendation} from "src/actions/RecommendationQueryAction";

interface StateProps {
    productRes: GetProductDataResponse,
    merchantProductRes: GetProductPageDataResponse,
    generalProductRes: GetProductPageDataResponse,
    currentCountryCodeRes: GetCurrentCountryCodeResponse,
    selectedProductRes: GetSelectedProductResponse,
}

interface OwnProps {
    readonly currentCountryCode: string,
    readonly metricsUtil?: MetricsUtil,
    handleCountryChange: (countryCode: string) => void
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    productRes: state.productRes,
    merchantProductRes: state.merchantProductRes,
    generalProductRes: state.generalProductRes,
    currentCountryCodeRes: state.currentCountryCodeRes,
    selectedProductRes: state.selectedProductRes,
});

interface DispatchProps {
    readonly dispatchGetProduct: (countryCode: string, searchKey: string) => void,
    readonly dispatchSelectedProduct: (countryCode: string, selectedProduct: ProductInfo) => void,
    readonly dispatchRemoveSelectedProduct: () => void,
    readonly dispatchRemoveProduct: () => void,
    readonly dispatchRemoveRecommendation: () => void,
}

const mapDispatchToProps = (dispatch: Dispatch) => (
    bindActionCreators({
        dispatchGetProduct: setProductDataQueryStart,
        dispatchSelectedProduct: setSelectedProductQueryStart,
        dispatchRemoveSelectedProduct: removeSelectedProduct,
        dispatchRemoveProduct: removeQueryProductInfo,
        dispatchRemoveRecommendation: removeRecommendation,
    }, dispatch)
);

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = StateProps & OwnProps & DispatchProps & WithTranslation;

interface State {
    searchKey: string
}

const emptyProductInfoResult: ProductInfoResult = {
    totalProductCount: 0,
    currentPage: 1,
    products: []
}

export class ExistingProductSearchComponent extends React.Component<Props, State> {

    protected readonly metricsUtil: MetricsUtil;

    constructor(props: Props) {
        super(props);

        this.state = {
            searchKey: ''
        };

        this.handleKeyup = this.handleKeyup.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.doSubmit = this.doSubmit.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleChangeCountry = this.handleChangeCountry.bind(this);
        this.handleSelect = this.handleSelect.bind(this);
        this.handleSelectMerchantProduct = this.handleSelectMerchantProduct.bind(this);
        this.handleSelectGeneralProduct = this.handleSelectGeneralProduct.bind(this);

        if (this.props.metricsUtil) {
            this.metricsUtil = this.props.metricsUtil;
        } else {
            this.metricsUtil = MetricsUtil.getDefaultMetricUtil();
        }
    }

    handleChange(event: KatInput.ChangeEvent) {
        this.setState({ searchKey: event.currentTarget.value });
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("searchKey", "input");
        }
    }

    handleChangeCountry(event: KatDropdown.ChangeEvent) {
        this.props.handleCountryChange(event.detail.value);
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("existingProduct_countryCode", "select");
        }
    }

    handleKeyup(event: KatInput.KeyupEvent) {
        let key = (event as unknown as KeyboardEvent).key;
        if (key === "Enter" || key === "NumpadEnter") {
            event.preventDefault();
            this.doSubmit();
        }
    }

    handleSubmit(event: React.FormEvent) {
        event.preventDefault();
        this.doSubmit();
    }

    doSubmit() {
        const countryCode = this.props.currentCountryCode;
        const searchKey = this.state.searchKey;
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("searchButton", "click",);
            this.metricsUtil.setLongTimer(ActionName.TIME_ON_PAGE_AFTER_PRODUCT_SEARCHING_OR_SELECTING);
        }
        if (!countryCode || !searchKey.trim()) {
            return;
        }
        this.props.dispatchGetProduct(countryCode, searchKey);
    }

    handleSelectMerchantProduct(product: ProductInfo) {
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("searchedMerchantProduct", "click");
        }
        this.handleSelect(product);
    }

    handleSelectGeneralProduct(product: ProductInfo) {
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("searchedGeneralProduct", "click");
        }
        this.handleSelect(product);
    }

    private handleSelect(product: ProductInfo) {
        const countryCode = this.hasProducts() ? this.props.productRes.productData.data.countryCode : this.props.currentCountryCode;
        if (countryCode != this.props.currentCountryCode) {
            this.props.handleCountryChange(countryCode);
        }
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("searchedProduct", "click");
        }
        if (product) {
            this.props.dispatchSelectedProduct(countryCode, product);
            if (this.metricsUtil) {
                this.metricsUtil.publishCounter("searchedProduct", "click");
                this.metricsUtil.setLongTimer(ActionName.TIME_ON_PAGE_AFTER_PRODUCT_SEARCHING_OR_SELECTING);
                this.metricsUtil.setShortTimer(ActionName.TIME_ON_ONE_PRODUCT);
            }
        } else {
            this.props.dispatchRemoveSelectedProduct();
            this.props.dispatchRemoveProduct();
            this.props.dispatchRemoveRecommendation();
        }
    }

    hasMerchantProducts(): boolean {
        return (this.props.productRes && this.props.productRes.requestStatus === RequestStatus.SUCCESS)
            || (this.props.merchantProductRes && this.props.merchantProductRes.requestStatus === RequestStatus.SUCCESS);
    }

    hasGeneralProducts(): boolean {
        return (this.props.productRes && this.props.productRes.requestStatus === RequestStatus.SUCCESS)
            || (this.props.generalProductRes && this.props.generalProductRes.requestStatus === RequestStatus.SUCCESS);
    }

    hasProducts(): boolean {
        return (this.props.productRes && this.props.productRes.requestStatus === RequestStatus.SUCCESS);
    }

    hasSelectedProducts(): boolean {
        return (this.props.selectedProductRes && this.props.selectedProductRes.requestStatus === RequestStatus.SUCCESS);
    }

    hasError(): boolean {
        const hasError = this.props.productRes && this.props.productRes.requestStatus === RequestStatus.ERROR;
        if (hasError && this.metricsUtil) {
            this.metricsUtil.publishCounter("searchedProducts", "error");
        }
        return hasError;
    }

    hasMerchantError(): boolean {
        const hasMerchantError = this.props.merchantProductRes && this.props.merchantProductRes.requestStatus === RequestStatus.ERROR;
        if (hasMerchantError && this.metricsUtil) {
            this.metricsUtil.publishCounter("searchedMerchantProducts", "error");
        }
        return hasMerchantError;
    }

    hasGeneralError(): boolean {
        const hasGeneralError = this.props.generalProductRes && this.props.generalProductRes.requestStatus === RequestStatus.ERROR;
        if (hasGeneralError && this.metricsUtil) {
            this.metricsUtil.publishCounter("searchedGeneralProducts", "error");
        }
        return hasGeneralError;
    }

    isMerchantInProgress(): boolean {
        return (this.props.productRes && this.props.productRes.requestStatus === RequestStatus.LOADING)
            || (this.props.merchantProductRes && this.props.merchantProductRes.requestStatus === RequestStatus.LOADING);
    }

    isGeneralInProgress(): boolean {
        return (this.props.productRes && this.props.productRes.requestStatus === RequestStatus.LOADING)
            || (this.props.generalProductRes && this.props.generalProductRes.requestStatus === RequestStatus.LOADING);
    }

    getMerchantProducts(): ProductInfoResult {
        if (this.props.merchantProductRes && this.props.merchantProductRes.requestStatus === RequestStatus.SUCCESS) {
            return this.props.merchantProductRes.productData.data;
        }
        if (this.props.productRes && this.props.productRes.requestStatus === RequestStatus.SUCCESS) {
            return this.props.productRes.productData.data.myProducts;
        }
        return emptyProductInfoResult;
    }

    getGeneralProducts(): ProductInfoResult {
        if (this.props.generalProductRes && this.props.generalProductRes.requestStatus === RequestStatus.SUCCESS) {
            return this.props.generalProductRes.productData.data;
        }
        if (this.props.productRes && this.props.productRes.requestStatus === RequestStatus.SUCCESS) {
            return this.props.productRes.productData.data.otherProducts;
        }
        return emptyProductInfoResult;
    }

    render() {
        const { t, productRes } = this.props;
        // productRes is not cleared
        const countryCode = this.hasProducts() ? productRes.productData.data.countryCode : this.props.currentCountryCode;
        const searchKey = this.hasProducts() ? productRes.productData.data.searchKey : this.state.searchKey;
        const hasError = this.hasError();

        return (
            <>
                {this.hasSelectedProducts() ? (<ProductDetailComponent handleCountryChange={this.props.handleCountryChange} metricsUtil={this.metricsUtil} countryCode={countryCode} handleSelect={this.handleSelect} />) :
                    (<div className="product-search-content-box">
                        <div>
                            <div id="ProductSearchFuncHeader" className="block">
                                <div className="product-search-element"><KatLabel text={t('SC_FBA_Calc_desc_search')} className="product-search-func-header" /></div>
                                <div className="product-search-element"><KatLabel text={t('SC_FBA_Calc_info_search')} className="revenue-calculator-header-content" /></div>
                            </div>
                            <form id="ProductSearchInput" onSubmit={this.handleSubmit} className="product-search-input">
                                <KatDropdown richSelectionLabel label={t('SC_FBA_Calc_label_country')} value={countryCode} className="dropdown-country" onChange={this.handleChangeCountry}>
                                    {
                                        getCountryListConfig().map((country, index) => (
                                            <KatOption value={country} key={index}>
                                                <div className="flexed">
                                                    <KatIcon name={("flag-" + country.toLowerCase()) as KatIcon.Name} size="small" />
                                                    <span className="paddedMedium">{country}</span>
                                                </div>
                                            </KatOption>
                                        ))
                                    }
                                </KatDropdown>
                                <KatInput label={t('SC_FBA_Calc_label_search')} tooltipText={t('SC_FBA_Calc_tip_searchAction')} placeholder={t('SC_FBA_Calc_tip_search')} className="input-asin" onChange={this.handleChange} onKeyup={this.handleKeyup} />
                                <KatButton label={t('SC_FBA_KnightsSearchButtonLabel_35008')} variant="primary" type="submit" disabled={productRes.requestStatus === RequestStatus.LOADING} className="button-search" />
                            </form>
                        </div>
                        <div className="product-search-element">
                            {isLoginMode() && !hasError && this.hasMerchantProducts() && <ProductListComponent metricsUtil={this.metricsUtil} countryCode={countryCode} searchKey={searchKey} productResults={this.getMerchantProducts()} isMyOffer={true} inLoading={this.isMerchantInProgress()} hasError={this.hasMerchantError()} handleSelect={this.handleSelectMerchantProduct} />}
                            {!hasError && this.hasGeneralProducts() && <ProductListComponent metricsUtil={this.metricsUtil} countryCode={countryCode} searchKey={searchKey} productResults={this.getGeneralProducts()} isMyOffer={false} inLoading={this.isGeneralInProgress()} hasError={this.hasGeneralError()} handleSelect={this.handleSelectGeneralProduct} />}
                            {hasError && <ErrorStatusComponent description={t('SC_FBA_Calc_error_productMatchFailed')} />}
                        </div>
                    </div>
                )}
            </>
        );
    }
}

export default withTranslation()(connector(ExistingProductSearchComponent));